/**
 * PostHog wrapper. Centralizes init + helpers so the rest of the app
 * never imports posthog-js directly. Three goals:
 *
 *   1. Safe to use even if posthog-js isn't installed yet (we lazy-import
 *      and if the module is missing, every helper becomes a no-op).
 *   2. Session replay is OFF by default. Each high-value moment opts in
 *      via `start_replay()` so we don't burn the 5K/month free quota
 *      recording dashboard sessions of paying customers.
 *   3. Read API key from REACT_APP_POSTHOG_KEY at build time. Skip init
 *      entirely in dev so local clicking doesn't pollute prod analytics.
 */

let ph = null;          // posthog instance once loaded
let initPromise = null; // memoized init promise

// Public/write-only project token — safe to ship in client bundles per
// PostHog docs. Env var wins if set (useful for swapping to a separate
// dev project later). No NODE_ENV gate — we want events to flow during
// local testing too. If you want to filter your own clicks out of prod
// analytics, add an "Internal users" filter in PostHog (Settings →
// Project → Internal users) keyed off your email after identify().
const POSTHOG_KEY  = process.env.REACT_APP_POSTHOG_KEY  || 'phc_cbwUCHqYbkbQQkT1Aby7GgbkDYsk9Np34oaadqmHGZi';

// Reverse proxy via our own domain so ad blockers can't drop events.
// The Express server forwards /relay-posthog/* → us.i.posthog.com/*.
// Locally we still need to talk to PostHog directly, so we point at
// us.i.posthog.com when running on localhost; in prod we use the proxy.
const isLocalhost = typeof window !== 'undefined'
  && /^(localhost|127\.0\.0\.1|0\.0\.0\.0)$/.test(window.location.hostname);
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST
  || (isLocalhost ? 'https://us.i.posthog.com' : `${window.location.origin}/relay-posthog`);
// ui_host points at the canonical PostHog UI for any in-product links
// (toolbar, opt-out screens). It's NOT used for capture — that all goes
// through api_host above.
const POSTHOG_UI_HOST = 'https://us.posthog.com';

const ENABLED      = !!POSTHOG_KEY;

const ensure_init = async () => {
  if (!ENABLED) return null;
  if (ph) return ph;
  if (!initPromise) {
    initPromise = (async () => {
      try {
        const mod = await import('posthog-js');
        const posthog = mod.default || mod;
        posthog.init(POSTHOG_KEY, {
          api_host: POSTHOG_HOST,
          ui_host: POSTHOG_UI_HOST,
          // Pins SDK behavior to the 2026-01-30 defaults set so future
          // posthog-js releases don't silently change capture semantics.
          // Bump intentionally when you've verified new behavior.
          defaults: '2026-01-30',
          person_profiles: 'identified_only',
          autocapture: true,
          capture_pageview: true,
          capture_performance: false,

          // Replays OFF by default — turned on per-page via start_replay().
          // PostHog free tier = 5,000 replays/month. Recording every session
          // would blow that on a busy day; recording only the moments that
          // matter (onboarding, pricing, GHL setup, first send) keeps us
          // well inside the limit and the captured replays are all useful.
          disable_session_recording: true,

          session_recording: {
            maskAllInputs: true,
            maskInputOptions: { password: true }
          }
        });
        ph = posthog;
        return ph;
      } catch (e) {
        // posthog-js not installed yet, or network failure loading the
        // SDK. All helpers below silently no-op — app keeps running.
        console.warn('posthog: init skipped —', e?.message || 'module not available');
        return null;
      }
    })();
  }
  return initPromise;
};

/**
 * Boot PostHog. Call once at app startup. Idempotent.
 */
export const init_posthog = () => { ensure_init(); };

/**
 * Mirror a track() call into PostHog. Safe to call before init resolves —
 * we await the promise then capture.
 */
export const ph_capture = (event_name, properties) => {
  ensure_init().then((p) => {
    if (p) {
      try { p.capture(event_name, properties || {}); } catch (e) {}
    }
  });
};

/**
 * Tie this browser to a real user_id. Call after login / signup so the
 * session replays are linked to a known account, not just an anon ID.
 */
export const identify_user = (user_id, properties) => {
  if (!user_id) return;
  ensure_init().then((p) => {
    if (p) {
      try { p.identify(String(user_id), properties || {}); } catch (e) {}
    }
  });
};

/**
 * Start session replay from this point on. Use sparingly — each call
 * counts toward the monthly replay quota. Use sessionStorage to dedupe
 * within a session so repeated mounts don't re-trigger.
 */
export const start_replay = (key = 'default') => {
  if (typeof window === 'undefined') return;
  try {
    const flag = `tl_replay_${key}`;
    if (sessionStorage.getItem(flag)) return;
    sessionStorage.setItem(flag, '1');
  } catch (e) {}
  ensure_init().then((p) => {
    if (p) {
      try { p.startSessionRecording(); } catch (e) {}
    }
  });
};

export const stop_replay = () => {
  ensure_init().then((p) => {
    if (p) {
      try { p.stopSessionRecording(); } catch (e) {}
    }
  });
};

/**
 * Reset on logout — clears the identified user so the next session
 * starts as anonymous.
 */
export const reset_posthog = () => {
  ensure_init().then((p) => {
    if (p) {
      try { p.reset(); } catch (e) {}
    }
  });
};
